import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LoginRequest } from "../../types";
import { Eye, EyeOff } from "lucide-react";
import { validateBusinessEmail, validateEmail } from "../../utils/validation";
import { getErrorMessage } from "../../utils/errorMessages";

type LoginPersona = "company" | "candidate";

const LoginPage: React.FC = () => {
  const { loginJobSeeker, loginCompanyUser, logout, loading, isAuthenticated, user } =
    useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [persona, setPersona] = useState<LoginPersona>("candidate");
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  const [formData, setFormData] = useState<LoginRequest>({
    email: "",
    password: "",
    remember_me: false,
  });
  const [fieldErrors, setFieldErrors] = useState({
    email: "",
    password: "",
  });
  // Debug: Log authentication state changes
  useEffect(() => {
    console.log("🔍 LoginPage: Auth state changed:", {
      isAuthenticated,
      user: user?.email,
      loading,
      loginAttempted,
    });

    // Only redirect if user is authenticated AND we haven't attempted login yet
    // This prevents conflicts with manual navigation in handleSubmit
    if (isAuthenticated && user && !loading && !loginAttempted) {
      // Determine appropriate dashboard based on user type
      let targetPath = "/dashboard"; // fallback

      if (user.is_super_admin) {
        // Super admin - redirect to super admin dashboard
        targetPath = "/super-admin/dashboard";
        console.log(
          "🔑 LoginPage: Super admin detected via useEffect, redirecting to super admin dashboard"
        );
      } else if (user.company_id || user.role) {
        // Company user - redirect to company dashboard

        targetPath = "/company/dashboard";
        console.log(
          "🏢 LoginPage: Company user detected via useEffect, redirecting to company dashboard"
        );
      } else {
        // Job seeker - redirect to candidate dashboard
        targetPath = "/candidate/dashboard";
        console.log(
          "👨‍💼 LoginPage: Job seeker detected via useEffect, redirecting to candidate dashboard"
        );
      }

      console.log(
        "🚀 LoginPage: User is authenticated, attempting navigation to:",
        targetPath
      );
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, user, loading, navigate, loginAttempted]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    let errorMsg = "";

    if (name === "email") {
      if (!value.trim()) {
        errorMsg = "Email is required";
      } else {
        const validation =
          persona === "company"
            ? validateBusinessEmail(value)
            : validateEmail(value);

        if (!validation.isValid) {
          errorMsg = validation.message || "Invalid email address";
        }
      }
    }

    if (name === "password") {
      if (!value.trim()) {
        errorMsg = "Password is required";
      }
    }

    setFieldErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      e.preventDefault();
      console.log("🚀 SUBMIT: Form submitted!", {
        email: formData.email,
        persona,
        hasPassword: !!formData.password,
      });

      // if (!formData.email || !formData.password) {
      //   console.log("❌ SUBMIT: Missing fields");
      //   setError("Please fill in all fields");
      //   return;
      // }
      if (!formData.email.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          email: "Email is required",
        }));
        return;
      }

      const emailValidation =
        persona === "company"
          ? validateBusinessEmail(formData.email)
          : validateEmail(formData.email);

      if (!emailValidation.isValid) {
        setFieldErrors((prev) => ({
          ...prev,
          email: emailValidation.message || "Invalid email address",
        }));
        return;
      }

      if (!formData.password.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          password: "Password is required",
        }));
        return;
      }
      setError("");
      setLoginAttempted(true); // Set flag to prevent useEffect redirect conflicts
      console.log(
        "🔑 LoginPage: Starting login process...",
        formData.email,
        "as",
        persona
      );

      // Use the appropriate login method based on persona
      if (persona === "candidate") {
        console.log("🔍 LoginPage: About to call loginJobSeeker function");
        const result = await loginJobSeeker(formData);
        console.log(
          "✅ LoginPage: Job seeker login successful, result:",
          result
        );

        if (result.requiresPasswordSetup) {
          console.log(
            "🔒 LoginPage: User needs password setup, logging out and redirecting to password setup page"
          );
          logout();
          navigate(
            `/auth/password-setup?email=${encodeURIComponent(
              result.email || formData.email
            )}&flow=job_seeker`,
            { replace: true }
          );
        } else {
          console.log(
            "✅ LoginPage: No password setup needed, navigating to candidate dashboard"
          );
          navigate("/candidate/dashboard", { replace: true });
        }
      } else {
        console.log("🔍 LoginPage: About to call loginCompanyUser function");

        try {
          const result = await loginCompanyUser(formData);
          console.log(
            "✅ LoginPage: Company login successful, result:",
            result
          );

          if (result.requiresPasswordSetup) {
            console.log(
              "🔒 LoginPage: Company user needs password setup, logging out and redirecting to password setup page"
            );
            logout();
            navigate(
              `/auth/password-setup?email=${encodeURIComponent(
                result.email || formData.email
              )}&flow=company_user`,
              { replace: true }
            );
          } else {
            console.log(
              "✅ LoginPage: No password setup needed, checking user type for redirect..."
            );

            // Check if user was actually set in context
            const authUser = JSON.parse(localStorage.getItem("user") || "null");
            console.log(
              "🔍 LoginPage: User in localStorage after login:",
              authUser
            );
            console.log("🔍 LoginPage: Auth context user after login:", user);
            console.log(
              "🔍 LoginPage: Auth context isAuthenticated after login:",
              isAuthenticated
            );

            // Let the useEffect handle the redirect based on user type
            // Don't manually redirect here to avoid conflicts
            setLoginAttempted(false); // Reset flag to allow useEffect redirect
          }
        } catch (loginError) {
          console.error("❌ LoginPage: Company login failed:", loginError);
          throw loginError; // Re-throw to be caught by outer try-catch
        }
      }

      console.log("🚀 LoginPage: Navigation called");
    } catch (err: any) {
      console.error("❌ LoginPage: Login failed with error:", err);
      console.error("❌ LoginPage: Error details:", {
        message: err.message,
        stack: err.stack,
        name: err.name,
      });

      console.error("❌ Login failed:", err);

      setError(getErrorMessage(err, "Login failed"));

      // setError(err.response?.data?.message || err.message || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-indigo-600 rounded-lg flex items-center justify-center">
            <svg
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome back to TalentKey.AI
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your account to continue
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-lg p-8">
          {error && (
            <div
              className={`mb-4 px-4 py-3 rounded-md ${
                error.includes("Access denied") ||
                error.includes("deactivated") ||
                error.includes("suspended")
                  ? "bg-orange-50 border border-orange-200 text-orange-700"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {error.includes("Access denied") ||
                  error.includes("deactivated") ||
                  error.includes("suspended") ? (
                    <svg
                      className="h-5 w-5 text-orange-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-red-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">
                    {error.includes("Access denied")
                      ? "Account Access Restricted"
                      : error}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Persona Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I am signing in as:
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPersona("company")}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    persona === "company"
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Company
                </button>
                <button
                  type="button"
                  onClick={() => setPersona("candidate")}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    persona === "candidate"
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Candidate
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="text"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                  fieldErrors.email
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                placeholder="Enter your email"
              />
              {fieldErrors.email && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            <div className="relative">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "password" : "text"}
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                  fieldErrors.password
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 pt-5 text-gray-500"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {fieldErrors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={formData.remember_me}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      remember_me: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/auth/forgot-password"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
            >
              {loading
                ? "Signing in..."
                : `Sign in as ${
                    persona === "candidate" ? "Candidate" : "Company User"
                  }`}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to={
                  persona === "candidate"
                    ? "/auth/candidate-signup"
                    : "/auth/company-signup"
                }
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Sign up as {persona === "candidate" ? "Job Seeker" : "Company"}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
